import { useState, useEffect, useRef } from "react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, Loader2 } from "lucide-react";

export default function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/portal/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size is 5MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        await api.post("/portal/documents", { filename: file.name, content_type: file.type, data_base64: base64 });
        toast.success("Document uploaded");
        load();
      } catch (err) {
        toast.error(errMsg(err));
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const download = async (id, filename, contentType) => {
    const { data } = await api.get(`/portal/documents/${id}`);
    const a = document.createElement("a");
    a.href = `data:${contentType};base64,${data.data_base64}`;
    a.download = filename;
    a.click();
  };

  const remove = async (id) => {
    await api.delete(`/portal/documents/${id}`);
    toast.success("Document removed");
    load();
  };

  return (
    <div data-testid="portal-documents-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl">Documents</h1>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="portal-document-file-input" />
        <Button data-testid="portal-upload-button" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
        </Button>
      </div>
      <div className="space-y-3">
        {docs.length === 0 && (
          <div className="rounded-xl border border-dashed p-10 text-center">
            <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Upload inventories, photos, contracts, or anything related to your jobs. Max 5MB per file.</p>
          </div>
        )}
        {docs.map((d) => (
          <div key={d.id} data-testid={`portal-document-${d.id}`} className="rounded-xl border bg-card p-4 flex items-center gap-4 card-lift">
            <FileText className="w-5 h-5 text-[var(--cc-accent)] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{d.filename}</p>
              <p className="text-xs text-muted-foreground font-mono-cc">{(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString()}</p>
            </div>
            <button data-testid={`portal-document-download-${d.id}`} onClick={() => download(d.id, d.filename, d.content_type)} className="p-2 rounded-lg hover:bg-secondary" aria-label="Download"><Download className="w-4 h-4" /></button>
            <button data-testid={`portal-document-delete-${d.id}`} onClick={() => remove(d.id)} className="p-2 rounded-lg hover:bg-secondary text-red-500" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
